import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

import { MFALoginPost, fetchMe } from "../../Redux/Thunks/AuthThunks";
import { ROUTES } from "../../constants";
import { resolvePostLoginNavigatePath } from "../../utils/authReturnUrl";
import logoImage from "../../assets/images/logoNew.jpg";
import UseTitle from "../../Utility/UseTitle/UseTitle";

const OTP_LENGTH = 6;

const shakeStyle = `
  @keyframes mfa-shake {
    0%,100%{transform:translateX(0)}
    20%,60%{transform:translateX(-6px)}
    40%,80%{transform:translateX(6px)}
  }
  .mfa-shake { animation: mfa-shake 0.4s ease; }
`;

export default function MfaLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const [session, setSession] = useState(null); // { userId, method, exp }
  const [shake, setShake] = useState(false);
  const [formError, setFormError] = useState("");

  const loading = useSelector((s) => s.apiStatus?.loading ?? {});
  const isSubmitting = loading["mfaVerifyBtn"] || loading["fetchMe"];

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: Object.fromEntries(
      Array.from({ length: OTP_LENGTH }, (_, i) => [`digit_${i}`, ""])
    ),
  });

  // ── Read and validate cookie on mount ─────────────────────────────────
  useEffect(() => {
    const raw = Cookies.get("userId");
    if (!raw) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }
    try {
      const decoded = JSON.parse(atob(raw));
      if (!decoded?.userId || !decoded?.exp || Date.now() > decoded.exp) {
        Cookies.remove("userId");
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }
      setSession(decoded);

      // Auto-redirect when session expires
      const msLeft = decoded.exp - Date.now();
      const timer = setTimeout(() => {
        Cookies.remove("userId");
        navigate(ROUTES.LOGIN, { replace: true });
      }, msLeft);

      return () => clearTimeout(timer);
    } catch {
      Cookies.remove("userId");
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [navigate]);

  // Focus the first input once session is known
  useEffect(() => {
    if (session) {
      inputRefs.current[0]?.focus();
    }
  }, [session]);

  // ── Keyboard handlers ──────────────────────────────────────────────────
  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.currentTarget.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleInputChange = (e, index, onChange) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    onChange(val);
    if (val && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    digits.split("").forEach((d, i) => setValue(`digit_${i}`, d));
    const lastFilled = Math.min(digits.length - 1, OTP_LENGTH - 1);
    inputRefs.current[lastFilled]?.focus();
  };

  // ── Submit ─────────────────────────────────────────────────────────────
  const onSubmit = async (values) => {
    const mfa_code = Array.from({ length: OTP_LENGTH }, (_, i) => values[`digit_${i}`]).join("");
    if (mfa_code.length < OTP_LENGTH) {
      setFormError("Please enter all 6 digits.");
      return;
    }
    setFormError("");

    try {
      const result = await dispatch(
        MFALoginPost({
          data: { mfa_code, userId: session.userId, method: session.method },
          buttonKey: "mfaVerifyBtn",
        })
      ).unwrap();

      // Store tokens and complete login
      localStorage.setItem("authToken", JSON.stringify(result));
      Cookies.remove("userId");

      let me;
      try {
        me = await dispatch(fetchMe()).unwrap();
      } catch {
        me = undefined;
      }

      navigate(resolvePostLoginNavigatePath(me, location), { replace: true });
    } catch (err) {
      // Shake animation
      setShake(true);
      setTimeout(() => setShake(false), 500);

      // Reset inputs and refocus first
      Array.from({ length: OTP_LENGTH }, (_, i) => setValue(`digit_${i}`, ""));
      inputRefs.current[0]?.focus();

      const msg =
        err?.errors?.mfa_code || err?.mfa_code || err?.message || "Invalid code. Please try again.";
      setFormError(msg);
    }
  };

  const handleBack = () => {
    Cookies.remove("userId");
    navigate(ROUTES.LOGIN, { replace: true });
  };

  if (!session) return null;

  const subtitle =
    session.method === "auth"
      ? "Enter code from Authenticator app"
      : "Enter code sent to your Email";

  return (
    <div>
      <style>{shakeStyle}</style>
      <UseTitle formattedTitle="MFA Verification" />
      <div className="auth-page-wrapper">
        <div className="auth-page-content pb-0">
          <div className="w-full max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-screen">
              <div className="w-full md:w-2/3 lg:w-1/2 xl:w-2/5">
                <div className="bg-surface-primary rounded-md border border-border-primary shadow-sm mt-4">
                  <div className="mt-5">
                    <div className="flex justify-center items-center">
                      <img
                        src={logoImage}
                        alt="Logo"
                        className="h-[72px] w-auto max-w-[280px] object-contain"
                      />
                    </div>
                  </div>
                  <div className="p-4 mt-4">
                    <h2 className="text-center text-lg font-semibold text-text-primary mb-1">
                      Two-Factor Verification
                    </h2>
                    <p className="text-center text-sm text-text-secondary mb-6">{subtitle}</p>

                    {formError && (
                      <div className="mb-4 px-3 py-2 rounded bg-red-50 border border-red-200 text-red-600 text-sm text-center">
                        {formError}
                      </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)}>
                      {/* OTP digit inputs */}
                      <div
                        className={`flex justify-center gap-2 mb-6 ${shake ? "mfa-shake" : ""}`}
                        onPaste={handlePaste}
                      >
                        {Array.from({ length: OTP_LENGTH }, (_, i) => (
                          <Controller
                            key={i}
                            name={`digit_${i}`}
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <input
                                ref={(el) => (inputRefs.current[i] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={value}
                                onChange={(e) => handleInputChange(e, i, onChange)}
                                onKeyDown={(e) => handleKeyDown(e, i)}
                                disabled={isSubmitting}
                                className="w-11 h-12 text-center text-xl font-semibold border border-border-primary rounded-lg bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                              />
                            )}
                          />
                        ))}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleBack}
                          disabled={isSubmitting}
                          className="flex-1 py-2 px-4 rounded-lg border border-border-primary text-text-primary bg-surface-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 py-2 px-4 rounded-lg bg-button-primary text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <svg
                                className="animate-spin h-4 w-4"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8H4z"
                                />
                              </svg>
                              Verifying…
                            </>
                          ) : (
                            "Verify Code"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
